import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import PaginationV2 from "../../../components/PaginationV2";
import QReader from "../../../components/QReader";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { XMarkIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import EditableOptionsCell from "../../../components/EditableOptionsCell";
import React from "react";
import { ChevronUpIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import useDialogStore from "../../../stores/useDialogStore";
import ColumnSearch from "../../../components/ColumnSearch";
import NoDataMessage from "../../../components/NoDataMessage";
import excelIcon from "../../../assets/excel-icon.webp";
import ColumnSelector from "../../../components/ColumnSelector";
import { QrCodeIcon } from "@heroicons/react/24/outline";
import EditableDateCell from "../../../components/EditableDateCell";
import { useEffect } from "react";
import ActionButtons from "../../../components/ActionButtons";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import EditSerialNumberCell from "../../../components/EditSerialNumberCell";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);

export default function SoldStock() {
  const { openDialog, closeDialog } = useDialogStore();
  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [exportInvoiceNumber, setExportInvoiceNumber] = useState(null);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [openDisclosure, setOpenDisclosure] = useState({
    invoiceNumber: null,
    invoiceId: null,
  });

  const { data: invoiceDetails, isLoading: invoiceDetailsLoading } = useQuery({
    queryKey: ["invoiceDetails", openDisclosure, location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const uniqueId = params.get("unique_id");
      const invoiceId =
        openDisclosure?.invoiceId || params.get("invoice_id") || null;

      const queryParam = uniqueId
        ? `unique_id=${uniqueId}&invoice_id=${invoiceId}`
        : "type=all";

      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/by-invoice-number/${openDisclosure.invoiceNumber}?${queryParam}`
      );

      return response.data;
    },
    enabled: !!openDisclosure?.invoiceNumber,
    keepPreviousData: true,
  });

  const handleDisclosureToggle = (invoiceNumber, invoiceId) => {
    if (openDisclosure?.invoiceNumber === invoiceNumber) {
      // Same item clicked → close it
      setOpenDisclosure({ invoiceNumber: null, invoiceId: null });
    } else {
      // Different item clicked → first close any open one
      setOpenDisclosure({ invoiceNumber: null, invoiceId: null });

      // After React renders the closed state, open the new one
      setTimeout(() => {
        setOpenDisclosure({ invoiceNumber, invoiceId });
      }, 150); // Allow React time to unmount the previous panel
    }
  };

  const [selectedColumns, setSelectedColumns] = useState({
    unique_id: true,
    brand: true,
    model: true,
    serial_no: true,
    counter_black: true,
    counter_color: true,
    total_counter: true,
    palette: true,
    created_at: true,
    options: true,
    comment: true,
    invoice_number: true,
    reserved_date: true,
    sold_date: true,
    confirmed: true,
    client_id: true,
    client: true,
    real_price: true,
  });

  const columnOptions = [
    { key: "unique_id", label: "Unique ID" },
    { key: "brand", label: "Brand" },
    { key: "model", label: "Model" },
    { key: "serial_no", label: "Serial No" },
    { key: "counter_black", label: "Counter Black" },
    { key: "counter_color", label: "Counter Color" },
    { key: "total_counter", label: "Total Counter" },
    { key: "palette", label: "Palette" },
    { key: "created_at", label: "Date Registered" },
    { key: "options", label: "Device Options" },
    { key: "comment", label: "Comment" },
    { key: "invoice_number", label: "Invoice Number" },
    { key: "reserved_date", label: "Reserved Date" },
    { key: "sold_date", label: "Sold Date" },
    { key: "confirmed", label: "Verified" },
    { key: "client_id", label: "Client ID" },
    { key: "client", label: "Client" },
    { key: "real_price", label: "Price" },
  ];

  const ColumnSelectorContent = ({ onExport }) => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);

    return (
      <div className="bg-white rounded-lg w-full p-4">
        <ColumnSelector
          columns={columnOptions}
          selectedColumns={localSelectedColumns}
          setSelectedColumns={setLocalSelectedColumns}
        />
        <div className="mt-6 flex justify-end space-x-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (
                Object.values(localSelectedColumns).some((selected) => selected)
              ) {
                setSelectedColumns(localSelectedColumns);
                onExport(localSelectedColumns);
                closeDialog();
              } else {
                toast.warning("Please select at least one column to export");
              }
            }}
          >
            {exportMutation?.isPending ? (
              <div className="flex items-center gap-2">
                <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : (
              "Export"
            )}
          </Button>
        </div>
      </div>
    );
  };

  const handleOpenColumnSelector = (invoiceNumber) => {
    setExportInvoiceNumber(invoiceNumber);

    openDialog({
      title: "Select Columns to Export",
      content: <ColumnSelectorContent onExport={handleExport} />,
    });
  };

  const exportMutation = useMutation({
    mutationFn: async (selectedColumns) => {
      const params = new URLSearchParams(location.search);
      // Ensure invoice number is sent real-time when exporting by invoice
      if (exportInvoiceNumber) {
        params.set("invoice_number", exportInvoiceNumber);
      }
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/sold-devices`,
        {
          columns: Object.entries(selectedColumns)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
          invoice_number: exportInvoiceNumber || null,
          year: selectedYear,
        },
        {
          responseType: "blob", // This tells Axios to expect a file
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Create filename with invoice number if available
      const date = new Date()
        .toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-");
      const invoiceNumber = exportInvoiceNumber;

      const filename = invoiceNumber
        ? `sold_stock_${date}_invoice_${invoiceNumber}.xlsx`
        : `sold_stock_export_${date}.xlsx`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up

      // Reset the invoice number after export
      setExportInvoiceNumber(null);

      handleClearFilters();
      closeDialog();
      toast.success("Report exported successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  const handleExport = async (selectedColumnss) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate(selectedColumnss);
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  // Fetch devices using React Query
  const { data: devicesData, isLoading } = useQuery({
    queryKey: ["sold-devices", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT
        }/devices/status/sold?${params.toString()}`
      );
      return response.data;
    },
    keepPreviousData: true,
  });

  // Edit device mutation
  const editCellMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["sold-devices"]);
      queryClient.invalidateQueries(["soldInvoiceDetails"]);
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  // Edit device status mutation
  const editDeviceStatusMutation = useMutation({
    mutationFn: ({ invoiceNumber, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/patch/${invoiceNumber}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["sold-devices"]);
      queryClient.invalidateQueries(["soldInvoiceDetails"]);
      toast.success("Device Status updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device status. Please try again.");
      console.error("Update device status error:", error);
    },
  });

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) => data.data.data.map(({ id, name }) => ({ id, name })),
  }).data;

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () =>
      axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?type=all`
      ),
    select: (data) =>
      data.data.data.data.map(({ id, name, device_brand_id }) => ({
        id,
        name,
        device_brand_id,
      })),
  }).data;

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["sold-devices"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["sold-devices"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["sold-devices"]);
  };

  const deleteDeviceMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`${window.REACT_APP_API_ENDPOINT}/devices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["sold-devices"]);
      toast.success("Device deleted successfully!");
    },
    onError: (error) => {
      console.error("Delete device error:", error);
    },
  });
  const handleDelete = (id) => {
    openDialog({
      title: "Delete Device",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to delete this device?
            <br />
            <span className="text-red-500 text-sm font-bold">
              (It will be moved to the trash bin)
            </span>
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={() => {
                deleteDeviceMutation.mutate(id);
                closeDialog();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  const paginationData = {
    current_page: devicesData?.current_page || 1,
    last_page: devicesData?.last_page || 1,
    next_page_url: devicesData?.next_page_url,
    prev_page_url: devicesData?.prev_page_url,
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
      <div className="sm:flex flex-col sm:items-start">
        <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
          <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
        </div>
      </div>
      <div className="mt-8 flow-root bg-white rounded-lg">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="w-full flex justify-between items-center py-3 px-4 bg-white rounded-lg">
              <h4 className="font-semibold text-2xl">Sold Stocks</h4>
              <div className="flex gap-2 items-center">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="year-filter"
                    className="text-sm font-medium text-gray-700 whitespace-nowrap"
                  >
                    Filter Excel data by year
                  </label>
                  <select
                    id="year-filter"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {YEAR_OPTIONS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<XMarkIcon className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="mb-2 lg:mb-0"
                >
                  Clear Filters
                </Button>
                <Button
                  variant="outline-success"
                  size="sm"
                  imgIcon={excelIcon}
                  onClick={() => handleOpenColumnSelector(null)}
                  className="mb-2 lg:mb-0"
                >
                  Export Sold Stock Data as CSV
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300 table-fixed">
                <thead className="bg-black">
                  <tr>
                    <th className="px-4 py-4 text-left text-white text-sm w-1/7">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Client ID"
                          columnKey="client_id"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Client ID..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm w-1/7">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Client"
                          columnKey="client"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Client..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm w-1/7">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Invoice Number"
                          columnKey="invoice_number"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Invoice..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm w-1/7">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Reserved Date"
                          columnKey="reserved_date"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          isDate={true}
                          placeholder="Search by Reserved Date..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm w-1/7">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Sold Date"
                          columnKey="sold_date"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          isDate={true}
                          placeholder="Search by Sold Date..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm w-1/7">
                      <div className="flex items-center gap-2">
                        Total Items Sold
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm w-1/7">
                      Actions
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm w-1/7"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {isLoading ? (
                    <tr>
                      <td colSpan={13} className="text-center">
                        <LoadingComponent />
                      </td>
                    </tr>
                  ) : devicesData?.data?.length == 0 ? (
                    <tr>
                      <td colSpan={13} className="text-center">
                        <NoData message="No sold devices found" />
                      </td>
                    </tr>
                  ) : (
                    devicesData?.data?.map((deviceStatus, index) => (
                      <React.Fragment key={deviceStatus.invoice_number}>
                        <tr
                          className={`cursor-pointer hover:bg-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-gray-100"
                            }`}
                          onClick={() =>
                            handleDisclosureToggle(
                              deviceStatus.invoice_number,
                              deviceStatus.invoice_id
                            )
                          }
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 w-1/7">
                            {deviceStatus?.client.unique_id || "---"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 w-1/7">
                            {deviceStatus?.client.name || "---"}
                          </td>
                          <td
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className="px-4 py-3 text-sm text-gray-900 w-1/7"
                          >
                            <EditableCell
                              value={deviceStatus.invoice_number || ""}
                              displayValue={deviceStatus.invoice_number || ""}
                              id={deviceStatus.id}
                              field="invoice_number"
                              onSave={(data) =>
                                editDeviceStatusMutation.mutate({
                                  invoiceNumber: deviceStatus.invoice_number,
                                  field: "invoice_number",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className="px-4 py-3 text-sm text-gray-900 w-1/7"
                          >
                            <EditableDateCell
                              value={deviceStatus.reserved_date}
                              displayValue={
                                deviceStatus.reserved_date
                                  ? dayjs
                                    .utc(deviceStatus.reserved_date)
                                    .format("DD/MM/YYYY")
                                  : "---"
                              }
                              id={deviceStatus.id}
                              field="reserved_date"
                              onSave={(data) =>
                                editDeviceStatusMutation.mutate({
                                  invoiceNumber: deviceStatus.invoice_number,
                                  field: "reserved_date",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className="px-4 py-3 text-sm text-gray-900 w-1/7"
                          >
                            <EditableDateCell
                              value={deviceStatus.sold_date}
                              displayValue={
                                deviceStatus.sold_date
                                  ? dayjs
                                    .utc(deviceStatus.sold_date)
                                    .format("DD/MM/YYYY")
                                  : "---"
                              }
                              id={deviceStatus.id}
                              field="sold_date"
                              onSave={(data) =>
                                editDeviceStatusMutation.mutate({
                                  invoiceNumber: deviceStatus.invoice_number,
                                  field: "sold_date",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 w-1/7">
                            {deviceStatus.total_devices || "---"}
                          </td>
                          <td className="px-4 py-3 text-gray-900 w-1/7">
                            <ChevronUpIcon
                              className={`h-5 w-5 transform transition-transform duration-200 ${openDisclosure?.invoiceId ===
                                deviceStatus.invoice_id
                                ? "rotate-180"
                                : ""
                                }`}
                            />
                          </td>
                          <td className="px-4 py-3 text-gray-900 w-1/7">
                            <div className="flex gap-2">
                              <Button
                                variant="outline-success"
                                size="sm"
                                imgIcon={excelIcon}
                                onClick={() => {
                                  handleOpenColumnSelector(
                                    deviceStatus.invoice_number
                                  );
                                }}
                                className="mb-2 lg:mb-0"
                              >
                                Export By Invoice Number
                              </Button>
                            </div>
                          </td>
                        </tr>

                        {openDisclosure?.invoiceId ===
                          deviceStatus.invoice_id && (
                            <tr>
                              <td colSpan={13} className="bg-gray-300 px-6 py-4">
                                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                                  <thead className="bg-bluePrimary text-white">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Unique ID
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Brand
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Model
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Serial No
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Counter Black
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Counter Color
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Total Counter
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Palette
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Registered Date
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Date Stayed
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Options
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Comment
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Reserved Date
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Price
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium uppercase w-1/14">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {invoiceDetailsLoading ? (
                                      <tr>
                                        <td colSpan={15} className="text-center">
                                          <LoadingComponent />
                                        </td>
                                      </tr>
                                    ) : Array.isArray(
                                      invoiceDetails?.data.data
                                    ) ? (
                                      invoiceDetails.data.data.map(
                                        (device, i) => (
                                          <tr
                                            key={i}
                                            className="hover:bg-gray-50"
                                          >
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/14">
                                              {device.unique_id}
                                            </td>

                                            <td className="whitespace-nowrap p-3 text-sm sm:pl-0 w-1/14">
                                              <div className="flex items-center pl-4">
                                                {brands && device.brand && (
                                                  <EditableSelectCell
                                                    value={device.device_brand_id}
                                                    displayValue={device.brand}
                                                    id={device.id}
                                                    options={brands}
                                                    field="brand"
                                                    onSave={(data) =>
                                                      editCellMutation.mutate({
                                                        deviceId: data.id,
                                                        field: "device_brand_id",
                                                        value: data.value,
                                                      })
                                                    }
                                                  />
                                                )}
                                              </div>
                                            </td>
                                            <td className="whitespace-nowrap p-3 text-sm text-black w-1/12">
                                              {models && device.model && (
                                                <EditableSelectCell
                                                  value={device.device_model_id}
                                                  displayValue={device.model}
                                                  id={device.id}
                                                  options={models.filter(
                                                    (model) =>
                                                      model.device_brand_id ===
                                                      device.device_brand_id
                                                  )}
                                                  field="device_model_id"
                                                  onSave={(data) =>
                                                    editCellMutation.mutate({
                                                      deviceId: data.id,
                                                      field: "device_model_id",
                                                      value: data.value,
                                                    })
                                                  }
                                                />
                                              )}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/12">
                                              <EditSerialNumberCell
                                                value={device.serial_no}
                                                displayValue={device.serial_no}
                                                id={device.id}
                                                field="serial_no"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "serial_no",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/12">
                                              <EditableCell
                                                value={device.counter_black}
                                                displayValue={
                                                  device.counter_black || "---"
                                                }
                                                id={device.id}
                                                field="counter_black"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "counter_black",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/12">
                                              <EditableCell
                                                value={device.counter_color}
                                                displayValue={
                                                  device.counter_color || "---"
                                                }
                                                id={device.id}
                                                field="counter_color"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "counter_color",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/12">
                                              <EditableCell
                                                value={device.total_counter}
                                                displayValue={
                                                  device.total_counter || "---"
                                                }
                                                id={device.id}
                                                field="total_counter"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "total_counter",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/12">
                                              <EditableCell
                                                value={device.palette}
                                                displayValue={
                                                  device.palette || "---"
                                                }
                                                id={device.id}
                                                field="palette"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "palette",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/12">
                                              {dayjs
                                                .utc(device.created_at)
                                                .format("DD-MM-YYYY")}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/12">
                                              {device.reserved_date &&
                                                device.created_at
                                                ? `${dayjs
                                                  .utc(device.reserved_date)
                                                  .diff(
                                                    dayjs.utc(
                                                      device.created_at
                                                    ),
                                                    "days"
                                                  )} days`
                                                : "---"}
                                            </td>

                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/12">
                                              <EditableOptionsCell
                                                deviceId={device.id}
                                                deviceOptions={
                                                  device.device_options || {}
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/12">
                                              <EditableCell
                                                value={device.comment || ""}
                                                displayValue={
                                                  device.comment || ""
                                                }
                                                id={device.id}
                                                field="comment"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "comment",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/14">
                                              {device.reserved_date
                                                ? `${dayjs
                                                  .utc(device.reserved_date)
                                                  .format("DD/MM/YYYY")}`
                                                : "---"}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 w-1/14">
                                              <EditableCell
                                                value={device.price || ""}
                                                displayValue={
                                                  device.price || "---"
                                                }
                                                id={device.id}
                                                field="price"
                                                onSave={(data) =>
                                                  editCellMutation.mutate({
                                                    deviceId: data.id,
                                                    field: "price",
                                                    value: data.value,
                                                  })
                                                }
                                              />
                                            </td>
                                            <td
                                              className="px-4 py-2 text-sm flex items-stretch gap-2 h-full text-gray-900"
                                              style={{ height: "100%" }}
                                            >
                                              <ActionButtons
                                                id={device.id}
                                                onDelete={() =>
                                                  handleDelete(device.id)
                                                }
                                              />
                                            </td>
                                          </tr>
                                        )
                                      )
                                    ) : (
                                      <tr>
                                        <td colSpan={15} className="text-center">
                                          <NoData message="No devices found for this invoice" />
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <PaginationV2
              current_page={paginationData.current_page}
              last_page={paginationData.last_page}
              next_page_url={paginationData.next_page_url}
              prev_page_url={paginationData.prev_page_url}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
